<template>
  <div>
    <div class="flex items-center">
      <div class="flex items-center">
        <input
          :id="inputId"
          type="checkbox"
          :name="name"
          :checked="!!checked"
          :disabled="disabled"
          :value="value"
          class="accent-primary rounded text-primary transition-all duration-100 ease-out"
          :class="[
            {
              'input-error': errorMessage,
            },

            size ? 'h-7 w-7 md:h-10 md:w-10' : 'h-6 w-6',
          ]"
          @change="onChange" />
        <label :for="inputId"></label>
      </div>
      <label
        :for="inputId"
        class="ml-4 text-secondary font-medium flex items-center gap-2 flex-1"
        :class="[
          { 'text-black': errorMessage },
          { 'text-sm font-medium md:text-2xl': size },
        ]">
        <slot name="label">{{ $t(label) }}</slot>
        <span v-if="required" class="text-danger dark:text-danger">*</span>
      </label>
    </div>
    <div v-if="single" class="pl-10">
      <span
        v-show="errorMessage"
        class="text-xs font-medium text-danger dark:text-danger">
        {{ errorMessage }}
      </span>
    </div>
  </div>
</template>

<script setup>
  import { useField, useFormValues } from 'vee-validate';
  import uniqueId from 'lodash/uniqueId.js';
  import get from 'lodash/get.js';
  import { computed } from 'vue';

  const emit = defineEmits([
    'keydown',
    'blur',
    'change',
    'input',
    'focus',
    'update:modelValue',
  ]);
  const props = defineProps({
    label: String,
    text: String,
    name: {
      type: String,
      default: '',
    },
    size: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    placeholder: String,
    component: {
      type: String,
      default: null,
    },
    cvalue: {
      type: [Boolean, String, Number],
      default: false,
    },
    modelValue: {
      type: [Boolean, String, Number],
      default: false,
    },
    btn: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    single: {
      type: Boolean,
      default: false,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    checkedValue: {
      type: [Boolean, String, Number],
      default: '',
    },
    uncheckedValue: {
      type: [Boolean, String, Number],
      default: '',
    },
    enabledControlled: Boolean,
    syncVModel: Boolean,
  });

  // const setSingleError = inject('setSingleError')

  const inputId = computed(() => props.id || uniqueId('checkbox'));

  // A checkbox that appears after the form was set up (a section shown by an
  // earlier answer) must not overwrite what the form already holds: vee-validate
  // prefers a field's initialValue over the form's value, so the default false
  // used to replace a whole list (own_transports: [] became false) and the
  // survey could not be submitted, with no red message to show why.
  const formValues = useFormValues();
  const formHasValue = get(formValues.value, props.name) !== undefined;

  const { value, errorMessage, handleChange, checked } = useField(
    () => props.name,
    undefined,
    {
      initialValue: formHasValue ? undefined : props.modelValue,
      type: 'checkbox',
      checkedValue: props.checkedValue,
      uncheckedValue: props.uncheckedValue,
      controlled: !props.enabledControlled,
      syncVModel: props.syncVModel,
    },
  );

  const onChange = e => {
    emit('change', e.target.checked, e);
    handleChange(e);
  };
</script>

<style scoped>
  .form-checkbox {
    box-shadow: unset !important;
  }
</style>
